import { ReactNode, useCallback, useState } from "react";

import type { SxProps, Theme } from "@mui/material/styles";

import { format } from "date-fns";

import { useAppDispatch } from "app/hooks";
import {
  setCustomMessage,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import {
  setContent as setConfirmContent,
  setOnChange as setConfirmOnChange,
  setOpen as setConfirmOpen,
  setOptions as setConfirmOptions,
  setTitle as setConfirmTitle
} from "components/confirm-dialog/confirm-dialog.slice";
import ErrorSnackbarContent from "components/error-snackbar-content";
import { toPhoneDigits } from "components/patient-search/new-patient-seed.utils";
import {
  LEAD_PHONE_CALL_ACTION_TYPE,
  LEAD_SOURCE_TYPE_SUNO_VOICE,
  PREFERRED_CHANNEL_TYPE_SMS
} from "constants/suno-comms";
import useEnsureCommsSession from "features/leads-ui/use-comms-session.hook";
import {
  AttachCallRequest,
  AttachCallResponse,
  type CallContact,
  type PracticeCallPatient,
  useAttachCallMutation,
  useDetachCallMutation
} from "services/dialstack";
import {
  useAddContactActionMutation,
  useCreateContactMutation
} from "services/suno-comms/suno-comms-api";

import { displayNumber } from "../call-display.util";
import AttachCallDialog from "./attach-call-dialog.component";
import { patientDisplayName } from "./caller-identity.util";
import QuickAddPatientDialog from "./quick-add-patient-dialog.component";
import RemoveFromPatientDialog from "./remove-from-patient-dialog.component";
import SaveContactDialog from "./save-contact-dialog.component";
import SaveLeadDialog, {
  SaveLeadInput,
  SaveLeadOutcome
} from "./save-lead-dialog.component";

export type UnknownCallerTarget = {
  // Backend Call.id. Null while the softphone only knows the provider id (no
  // pop yet, or one from a backend that predates the field); actions stay
  // disabled until the pop, or the lookup by provider id, brings it.
  callId: number | null;
  // The other party's number — from_number inbound, to_number outbound.
  remoteNumber: string;
  // Carrier caller-ID name (CNAM), used to seed the New Patient form.
  fromName: string | null;
  // The clinic the call was resolved to; a lead saved from it files under
  // that clinic's comms location (ENG-2177). Null = unknown here.
  clinicId?: number | null;
  // When the call started, logged on the lead with the call.
  startedAt?: string | null;
  // The patient and contact on the call now, for Remove from patient
  // (ENG-2563): the dialog names both, and the overlay keeps them current.
  patient?: PracticeCallPatient | null;
  contact?: CallContact | null;
  // The call's number sits in the patient's secondary phone (the detail's
  // `number_remembered_on_patient`), so Remove offers to take it off too.
  numberRemembered?: boolean;
};

type AttachTarget = UnknownCallerTarget & { callId: number };

// Quick Add created the patient but the attach request failed: the patient
// exists, so a retry must be attach-only (never a second create).
export type AttachRecovery = {
  callId: number;
  createdPatientId: number;
  patientName: string;
};

export type AttachError = { error: unknown };
export type AttachOutcome = AttachError | null;

type OpenDialog = {
  kind: "quickAdd" | "attach" | "contact" | "lead" | "removePatient";
  // Snapshotted when opened, so a new call arriving (or the live call ending)
  // under an open dialog cannot redirect the attach to the wrong call.
  target: AttachTarget;
};

export type UnknownCallerActions = {
  callId: number | null;
  openQuickAdd: () => void;
  openAttach: () => void;
  openSaveContact: () => void;
  // Files the caller as a prospective patient in Suno Leads (ENG-2177).
  openSaveLead: () => void;
  // The lead this call was saved as, for the rest of the call's lifetime;
  // the row then offers "View lead" instead of "Save as lead".
  leadId: string | null;
  // Takes a mis-attached contact off the call, after a confirm (ENG-2177).
  removeContact: () => void;
  // Takes a patient staff attached by hand off the call (ENG-2563).
  removePatient: () => void;
  // Set only while it concerns the current call.
  recovery: AttachRecovery | null;
  retryAttach: () => Promise<void>;
  dismissRecovery: () => void;
  isAttaching: boolean;
  isDetaching: boolean;
};

type Options = UnknownCallerTarget & {
  onAttached?: (response: AttachCallResponse) => void;
  onDetached?: () => void;
  // For a host whose own chrome floats above modals (the softphone widget):
  // lifts the dialogs over the panel they were opened from.
  dialogSx?: SxProps<Theme>;
};

// What the lead's activity log says about the call that created it.
export function leadCallNote(startedAt: string | null | undefined): string {
  return startedAt
    ? `Suno Voice call on ${format(new Date(startedAt), "MMM d, yyyy h:mm a")}`
    : "Suno Voice call";
}

// Owns the four unknown-caller dialogs and the attach mutation for one call.
// `dialogs` must be rendered by a HOST that outlives the caller display — the
// widget root, the drawer, the workspace pane — never inside a live-call card
// that unmounts on hangup, or the form vanishes mid-edit.
export default function useUnknownCallerActions({
  callId,
  remoteNumber,
  fromName,
  clinicId = null,
  startedAt = null,
  patient = null,
  contact = null,
  numberRemembered = false,
  onAttached,
  onDetached,
  dialogSx
}: Options): { actions: UnknownCallerActions; dialogs: ReactNode } {
  const dispatch = useAppDispatch();
  const [attachCall, { isLoading: isAttaching }] = useAttachCallMutation();
  const [detachCall, { isLoading: isDetaching }] = useDetachCallMutation();
  const [createContact] = useCreateContactMutation();
  const [addContactAction] = useAddContactActionMutation();
  const ensureCommsSession = useEnsureCommsSession();
  const [dialog, setDialog] = useState<OpenDialog | null>(null);
  const [recovery, setRecovery] = useState<AttachRecovery | null>(null);
  // Lead id by backend call id, so a re-render (or reopening the same call)
  // keeps offering "View lead" rather than a second save.
  const [leads, setLeads] = useState<Record<number, string>>({});

  const notify = useCallback(
    (message: ReactNode) => {
      dispatch(setCustomMessage(message));
      dispatch(setOpen(true));
    },
    [dispatch]
  );

  const open = (kind: OpenDialog["kind"]) => {
    if (callId === null) return;
    setDialog({
      kind,
      target: {
        callId,
        remoteNumber,
        fromName,
        clinicId,
        startedAt,
        patient,
        contact,
        numberRemembered
      }
    });
  };
  const closeDialog = useCallback(() => setDialog(null), []);

  // Creates the lead in comms, then logs the call on it. The comms base
  // query needs a JWT in the leads slice, which only the leads routes mint,
  // so one is ensured first. Comms de-duplicates by number: a number that is
  // already a lead comes back as a new row pointing at the canonical one,
  // and that is the lead the user is sent to.
  const saveLead = useCallback(
    async (
      target: AttachTarget,
      input: SaveLeadInput
    ): Promise<SaveLeadOutcome> => {
      if (!(await ensureCommsSession())) {
        return { error: { status: "CUSTOM_ERROR", error: "comms_session" } };
      }
      const response = await createContact({
        first_name: input.first_name,
        last_name: input.last_name,
        preferred_channel: PREFERRED_CHANNEL_TYPE_SMS,
        numbers: [
          {
            priority: 1,
            type: "1",
            phone_number:
              toPhoneDigits(target.remoteNumber) || target.remoteNumber
          }
        ],
        lead_source_type: LEAD_SOURCE_TYPE_SUNO_VOICE,
        ...(input.locationId ? { location_id: input.locationId } : {})
      });
      if ("error" in response) return response;
      const leadId = String(
        response.data.canonical_contact_id ?? response.data.id
      );
      // The lead exists either way; a failed log is not worth a second save.
      await addContactAction({
        contact_id: leadId,
        type: LEAD_PHONE_CALL_ACTION_TYPE,
        note: leadCallNote(target.startedAt)
      });
      setLeads((prev) => ({ ...prev, [target.callId]: leadId }));
      notify("Saved as a lead");
      return null;
    },
    [addContactAction, createContact, ensureCommsSession, notify]
  );

  // Every attach goes through here so success reporting is identical across
  // the three dialogs. Resolves null on success or the error response; the
  // error is surfaced here unless the dialog handles it itself.
  const attach = useCallback(
    async (
      target: AttachTarget,
      request: Omit<AttachCallRequest, "callId">,
      successMessage: (response: AttachCallResponse) => string,
      { silentError = false } = {}
    ): Promise<AttachOutcome> => {
      const response = await attachCall({ callId: target.callId, ...request });
      if ("error" in response) {
        if (!silentError) notify(<ErrorSnackbarContent response={response} />);
        return response;
      }
      setRecovery(null);
      notify(successMessage(response.data));
      onAttached?.(response.data);
      return null;
    },
    [attachCall, notify, onAttached]
  );

  const attachCreatedPatient = useCallback(
    async (target: AttachTarget, patientId: number, patientName: string) => {
      const error = await attach(
        target,
        { patient_id: patientId },
        () => `${patientName} was added and this call was attached`,
        { silentError: true }
      );
      if (error) {
        setRecovery({
          callId: target.callId,
          createdPatientId: patientId,
          patientName
        });
      }
      return error === null;
    },
    [attach]
  );

  const retryAttach = useCallback(async () => {
    if (!recovery) return;
    await attachCreatedPatient(
      { callId: recovery.callId, remoteNumber, fromName },
      recovery.createdPatientId,
      recovery.patientName
    );
  }, [attachCreatedPatient, fromName, recovery, remoteNumber]);

  const dismissRecovery = useCallback(() => setRecovery(null), []);

  // The confirm goes through the shared dialog; the detach itself writes the
  // cleared fields into the overlay (see detachCall in services/dialstack).
  const removeContact = useCallback(() => {
    if (callId === null) return;
    const target = callId;
    dispatch(setConfirmTitle("Remove the contact from this call?"));
    dispatch(
      setConfirmContent(
        "The call goes back to an unknown caller. The contact itself is kept, and future calls from its numbers still match it."
      )
    );
    dispatch(
      setConfirmOptions({
        actionButtonText: "Remove contact",
        actionButtonType: "danger"
      })
    );
    dispatch(
      setConfirmOnChange(async () => {
        const response = await detachCall({ callId: target });
        if ("error" in response) {
          notify(<ErrorSnackbarContent response={response} />);
          return;
        }
        notify("Contact removed from the call");
      })
    );
    dispatch(setConfirmOpen(true));
  }, [callId, detachCall, dispatch, notify]);

  // The patient's removal has its own dialog: it can also take the number
  // off the patient's record, which the shared confirm cannot ask.
  const removePatient = useCallback(
    async (target: AttachTarget, forgetNumber: boolean) => {
      const response = await detachCall({
        callId: target.callId,
        forget_number: forgetNumber
      });
      if ("error" in response) {
        notify(<ErrorSnackbarContent response={response} />);
        return response;
      }
      const name = target.patient
        ? patientDisplayName(target.patient)
        : "the patient";
      notify(
        response.data.number_forgotten
          ? `Removed from ${name}, and the number was taken off their record`
          : `Removed from ${name}`
      );
      onDetached?.();
      return null;
    },
    [detachCall, notify, onDetached]
  );

  const dialogs = dialog && (
    <>
      {dialog.kind === "removePatient" && dialog.target.patient && (
        <RemoveFromPatientDialog
          patientName={patientDisplayName(dialog.target.patient)}
          callerName={
            dialog.target.contact?.name ??
            displayNumber(dialog.target.remoteNumber)
          }
          remoteNumber={dialog.target.remoteNumber}
          numberRemembered={Boolean(dialog.target.numberRemembered)}
          onClose={closeDialog}
          onRemove={(forgetNumber) =>
            removePatient(dialog.target, forgetNumber)
          }
          sx={dialogSx}
        />
      )}
      {dialog.kind === "quickAdd" && (
        <QuickAddPatientDialog
          remoteNumber={dialog.target.remoteNumber}
          fromName={dialog.target.fromName}
          recovery={recovery?.callId === dialog.target.callId ? recovery : null}
          isAttaching={isAttaching}
          onClose={closeDialog}
          onCreated={(patientId, patientName) =>
            attachCreatedPatient(dialog.target, patientId, patientName)
          }
          onRetryAttach={retryAttach}
          sx={dialogSx}
        />
      )}
      {dialog.kind === "attach" && (
        <AttachCallDialog
          remoteNumber={dialog.target.remoteNumber}
          onClose={closeDialog}
          sx={dialogSx}
          onAttach={(request) =>
            attach(dialog.target, request, ({ number_remembered }) =>
              number_remembered
                ? "Call attached and number saved to the patient"
                : "Call attached to the patient"
            )
          }
        />
      )}
      {dialog.kind === "contact" && (
        <SaveContactDialog
          remoteNumber={dialog.target.remoteNumber}
          fromName={dialog.target.fromName}
          onClose={closeDialog}
          sx={dialogSx}
          onAttach={(request) =>
            attach(
              dialog.target,
              request,
              ({ call }) =>
                `Call attached to ${call.contact?.name ?? "the contact"}`,
              { silentError: true }
            )
          }
        />
      )}
      {dialog.kind === "lead" && (
        <SaveLeadDialog
          remoteNumber={dialog.target.remoteNumber}
          fromName={dialog.target.fromName}
          clinicId={dialog.target.clinicId ?? null}
          onClose={closeDialog}
          onSave={(input) => saveLead(dialog.target, input)}
          sx={dialogSx}
        />
      )}
    </>
  );

  return {
    actions: {
      callId,
      openQuickAdd: () => open("quickAdd"),
      openAttach: () => open("attach"),
      openSaveContact: () => open("contact"),
      openSaveLead: () => open("lead"),
      leadId: callId === null ? null : (leads[callId] ?? null),
      removeContact,
      removePatient: () => open("removePatient"),
      recovery: recovery?.callId === callId ? recovery : null,
      retryAttach,
      dismissRecovery,
      isAttaching,
      isDetaching
    },
    dialogs
  };
}
